import { createSignal, createEffect, onMount, onCleanup } from 'solid-js';
import type { ThemePreset, ThemeMode } from './types';
import { themePresets, defaultPreset } from './presets';

// Storage keys
const STORAGE_KEY_PRESET = 'theme-preset';
const STORAGE_KEY_MODE = 'theme-mode';

// Get system preference
const getSystemPrefersDark = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const getReducedMotion = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Storage helpers
const storage = {
  getPreset: (): ThemePreset | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    const stored = localStorage.getItem(STORAGE_KEY_PRESET);
    return stored && stored in themePresets ? (stored as ThemePreset) : null;
  },
  setPreset: (preset: ThemePreset) => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(STORAGE_KEY_PRESET, preset);
  },
  getMode: (): ThemeMode | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    const stored = localStorage.getItem(STORAGE_KEY_MODE);
    return stored === 'light' || stored === 'dark' ? stored : null;
  },
  setMode: (mode: ThemeMode) => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(STORAGE_KEY_MODE, mode);
  },
};

// Apply theme to document
const applyTheme = (preset: ThemePreset, mode: ThemeMode) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const presetConfig = themePresets[preset] ?? defaultPreset;
  const tokens = mode === 'dark' ? presetConfig.dark : presetConfig.light;

  // Set data attributes
  root.setAttribute('data-theme', preset);
  root.setAttribute('data-mode', mode);

  // Apply CSS custom properties
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

// Announce theme change to screen readers
const announceThemeChange = (preset: string, mode: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  const liveRegion = document.getElementById('aria-live-region');
  if (liveRegion) {
    liveRegion.textContent = `Theme changed to ${preset.replace(/-/g, ' ')} in ${mode} mode`;
  }
};

// Create theme store
export function createThemeStore() {
  // Initialize signals
  const [preset, setPreset] = createSignal<ThemePreset>(defaultPreset.name);
  const [mode, setMode] = createSignal<ThemeMode>('dark');
  const [systemPrefersDark, setSystemPrefersDark] = createSignal(false);
  const [reducedMotion, setReducedMotion] = createSignal(false);

  // Initialize on mount
  onMount(() => {
    // Check system preferences
    setSystemPrefersDark(getSystemPrefersDark());
    setReducedMotion(getReducedMotion());

    // Load from storage or use system defaults
    const storedPreset = storage.getPreset();
    const storedMode = storage.getMode();

    const initialPreset = storedPreset ?? defaultPreset.name;
    const initialMode = storedMode ?? (getSystemPrefersDark() ? 'dark' : 'light');

    setPreset(initialPreset);
    setMode(initialMode);

    // Apply initial theme
    applyTheme(initialPreset, initialMode);

    // Listen for system preference changes
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleDarkModeChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
      // Only auto-switch if user hasn't manually set a preference
      if (!storage.getMode()) {
        const newMode = e.matches ? 'dark' : 'light';
        setMode(newMode);
        applyTheme(preset(), newMode);
      }
    };

    const handleMotionChange = (e: MediaQueryListEvent) => {
      setReducedMotion(e.matches);
    };

    darkModeQuery.addEventListener('change', handleDarkModeChange);
    motionQuery.addEventListener('change', handleMotionChange);

    onCleanup(() => {
      darkModeQuery.removeEventListener('change', handleDarkModeChange);
      motionQuery.removeEventListener('change', handleMotionChange);
    });
  });

  // Apply theme when preset or mode changes
  createEffect(() => {
    const currentPreset = preset();
    const currentMode = mode();
    applyTheme(currentPreset, currentMode);
  });

  // Actions
  const setThemePreset = (newPreset: ThemePreset) => {
    setPreset(newPreset);
    storage.setPreset(newPreset);
    announceThemeChange(newPreset, mode());
  };

  const setThemeMode = (newMode: ThemeMode) => {
    setMode(newMode);
    storage.setMode(newMode);
    announceThemeChange(preset(), newMode);
  };

  const toggleMode = () => {
    const newMode = mode() === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  };

  // Get current preset config
  const currentPresetConfig = () => themePresets[preset()] ?? defaultPreset;

  // Get current tokens
  const currentTokens = () => {
    const config = currentPresetConfig();
    return mode() === 'dark' ? config.dark : config.light;
  };

  return {
    // State
    preset,
    mode,
    systemPrefersDark,
    reducedMotion,

    // Getters
    currentPresetConfig,
    currentTokens,

    // Actions
    setPreset: setThemePreset,
    setMode: setThemeMode,
    toggleMode,

    // Preset list for UI
    availablePresets: Object.values(themePresets).map(p => ({
      name: p.name,
      label: p.label,
      description: p.description,
    })),
  };
}

// Type for the theme store
export type ThemeStore = ReturnType<typeof createThemeStore>;
